import type {
  MultiplayerRoomDelta,
  MultiplayerRoomSnapshot,
} from "./types.ts";
import { MULTIPLAYER_CONFIG } from "../../config/multiplayer.ts";

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function changedRecords<T>(
  previous: Readonly<Record<string, T>>,
  current: Readonly<Record<string, T>>,
): Readonly<Record<string, T>> | undefined {
  const changed: Record<string, T> = {};
  for (const [id, value] of Object.entries(current)) {
    if (!sameJson(previous[id], value)) changed[id] = value;
  }
  return Object.keys(changed).length === 0 ? undefined : changed;
}

export function createMultiplayerRoomDelta(
  previous: Readonly<MultiplayerRoomSnapshot>,
  current: Readonly<MultiplayerRoomSnapshot>,
): MultiplayerRoomDelta {
  const changedBoats = changedRecords(previous.boats.boatsById, current.boats.boatsById);
  const changedVehicles = changedRecords(
    previous.roadVehicles.vehiclesById,
    current.roadVehicles.vehiclesById,
  );
  const previousEventIds = new Set(previous.semanticEvents.map((event) => event.id));
  const addedSemanticEvents = current.semanticEvents.filter((event) =>
    !previousEventIds.has(event.id)
  );
  return {
    game: current.game,
    boats: {
      tick: current.boats.tick,
      ...(changedBoats === undefined ? {} : { boatsById: changedBoats }),
      ...(sameJson(
          previous.boats.mountedBoatIdByPlayerId,
          current.boats.mountedBoatIdByPlayerId,
        )
        ? {}
        : { mountedBoatIdByPlayerId: current.boats.mountedBoatIdByPlayerId }),
    },
    roadVehicles: {
      tick: current.roadVehicles.tick,
      ...(changedVehicles === undefined ? {} : { vehiclesById: changedVehicles }),
      ...(sameJson(
          previous.roadVehicles.mountByPlayerId,
          current.roadVehicles.mountByPlayerId,
        )
        ? {}
        : { mountByPlayerId: current.roadVehicles.mountByPlayerId }),
    },
    publicTransport: current.publicTransport,
    ...(sameJson(previous.membersByPlayerId, current.membersByPlayerId)
      ? {}
      : { membersByPlayerId: current.membersByPlayerId }),
    ...(sameJson(previous.appearancesByPlayerId, current.appearancesByPlayerId)
      ? {}
      : { appearancesByPlayerId: current.appearancesByPlayerId }),
    ...(sameJson(previous.neighborhood, current.neighborhood)
      ? {}
      : { neighborhood: current.neighborhood }),
    ...(sameJson(previous.traversalByPlayerId, current.traversalByPlayerId)
      ? {}
      : { traversalByPlayerId: current.traversalByPlayerId }),
    ...(sameJson(previous.interiorByPlayerId, current.interiorByPlayerId)
      ? {}
      : { interiorByPlayerId: current.interiorByPlayerId }),
    ...(sameJson(previous.elevatorByPlayerId, current.elevatorByPlayerId)
      ? {}
      : { elevatorByPlayerId: current.elevatorByPlayerId }),
    ...(sameJson(previous.residents, current.residents)
      ? {}
      : { residents: current.residents }),
    ...(addedSemanticEvents.length === 0
      ? {}
      : { semanticEvents: addedSemanticEvents }),
    ...(sameJson(previous.environment, current.environment)
      ? {}
      : { environment: current.environment }),
  };
}

export function applyMultiplayerRoomDelta(
  current: Readonly<MultiplayerRoomSnapshot>,
  delta: Readonly<MultiplayerRoomDelta>,
): MultiplayerRoomSnapshot {
  return {
    ...current,
    game: delta.game,
    boats: {
      ...current.boats,
      tick: delta.boats.tick,
      boatsById: delta.boats.boatsById === undefined
        ? current.boats.boatsById
        : { ...current.boats.boatsById, ...delta.boats.boatsById },
      mountedBoatIdByPlayerId:
        delta.boats.mountedBoatIdByPlayerId ?? current.boats.mountedBoatIdByPlayerId,
    },
    roadVehicles: {
      ...current.roadVehicles,
      tick: delta.roadVehicles.tick,
      vehiclesById: delta.roadVehicles.vehiclesById === undefined
        ? current.roadVehicles.vehiclesById
        : {
            ...current.roadVehicles.vehiclesById,
            ...delta.roadVehicles.vehiclesById,
          },
      mountByPlayerId:
        delta.roadVehicles.mountByPlayerId ?? current.roadVehicles.mountByPlayerId,
    },
    publicTransport: delta.publicTransport,
    membersByPlayerId: delta.membersByPlayerId ?? current.membersByPlayerId,
    appearancesByPlayerId:
      delta.appearancesByPlayerId ?? current.appearancesByPlayerId,
    neighborhood: delta.neighborhood ?? current.neighborhood,
    traversalByPlayerId:
      delta.traversalByPlayerId ?? current.traversalByPlayerId,
    interiorByPlayerId: delta.interiorByPlayerId ?? current.interiorByPlayerId,
    elevatorByPlayerId: delta.elevatorByPlayerId ?? current.elevatorByPlayerId,
    residents: delta.residents ?? current.residents,
    semanticEvents: delta.semanticEvents === undefined
      ? current.semanticEvents
      : [
          ...current.semanticEvents,
          ...delta.semanticEvents.filter((event) =>
            !current.semanticEvents.some((existing) => existing.id === event.id)
          ),
        ].slice(-MULTIPLAYER_CONFIG.semanticEventHistoryLimit),
    environment: delta.environment ?? current.environment,
  };
}
